<!-- src/components/GlobalMenuButton.vue -->
<!-- Global floating button that allows users to view menus from any page -->
<template>
  <div class="fixed bottom-6 right-6 z-40">
    <!-- Expanded Menu Selection Panel -->
    <transition name="slide-up">
      <div
        v-if="isExpanded"
        class="absolute bottom-16 right-0 w-72 bg-white shadow-2xl overflow-hidden mb-2"
      >
        <!-- Panel Header -->
        <div class="bg-sg px-4 py-3 flex justify-between items-center">
          <h3 class="rl text-white text-lg font-bold">View Menus</h3>
          <button
            @click="isExpanded = false"
            class="text-white hover:text-gray-200 transition-colors"
            aria-label="Close menu panel"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Menu Options -->
        <div class="p-2">
          <!-- Brunch Menu Option -->
          <button
            @click="openMenu('brunch')"
            class="w-full flex items-center p-3 hover:bg-gray-100 transition-colors duration-200 group"
          >
            <div class="w-12 h-12 overflow-hidden mr-3 shadow-sm">
              <img
                :src="menuImages.brunch[0]"
                alt="Brunch Menu"
                class="w-full h-full object-cover object-top"
              />
            </div>
            <div class="text-left">
              <span
                class="rl text-gray-800 font-medium group-hover:text-sg transition-colors"
                >Brunch Menu</span
              >
              <span class="block text-xs text-gray-500">1 Page</span>
            </div>
          </button>

          <!-- Dinner Menu Option -->
          <button
            @click="openMenu('dinner')"
            class="w-full flex items-center p-3 hover:bg-gray-100 transition-colors duration-200 group"
          >
            <div class="w-12 h-12 overflow-hidden mr-3 shadow-sm">
              <img
                :src="menuImages.dinner[0]"
                alt="Dinner Menu"
                class="w-full h-full object-cover object-top"
              />
            </div>
            <div class="text-left">
              <span
                class="rl text-gray-800 font-medium group-hover:text-sg transition-colors"
                >Dinner Menu</span
              >
              <span class="block text-xs text-gray-500">1 Page</span>
            </div>
          </button>

          <!-- Dessert Menu Option -->
          <button
            @click="openMenu('dessert')"
            class="w-full flex items-center p-3 hover:bg-gray-100 transition-colors duration-200 group"
          >
            <div class="w-12 h-12 overflow-hidden mr-3 shadow-sm">
              <img
                :src="menuImages.dessert[0]"
                alt="Dessert Menu"
                class="w-full h-full object-cover object-top"
              />
            </div>
            <div class="text-left">
              <span
                class="rl text-gray-800 font-medium group-hover:text-sg transition-colors"
                >Dessert Menu</span
              >
              <span class="block text-xs text-gray-500">1 Page</span>
            </div>
          </button>

          <!-- Drinks Menu Option -->
          <button
            @click="openMenu('drinks')"
            class="w-full flex items-center p-3 hover:bg-gray-100 transition-colors duration-200 group"
          >
            <div class="w-12 h-12 overflow-hidden mr-3 shadow-sm">
              <img
                :src="menuImages.drinks[0]"
                alt="Drinks Menu"
                class="w-full h-full object-cover object-top"
              />
            </div>
            <div class="text-left">
              <span
                class="rl text-gray-800 font-medium group-hover:text-sg transition-colors"
                >Drinks Menu</span
              >
              <span class="block text-xs text-gray-500">2 Pages</span>
            </div>
          </button>
        </div>
      </div>
    </transition>

    <!-- Main Floating Button -->
    <button
      @click="toggleExpanded"
      class="flex items-center justify-center bg-sg hover:bg-pb text-white px-4 py-3 shadow-lg hover:shadow-xl transition-all duration-300 group"
      aria-label="View Menus"
    >
      <!-- Menu Icon -->
      <svg
        class="w-5 h-5 mr-2"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
      <span class="rl text-sm font-medium">View Menus</span>
    </button>

    <!-- Menu Viewer Modal - with cross-menu navigation -->
    <MenuViewer
      :isOpen="viewerOpen"
      :initialMenuIndex="initialMenuIndex"
      @close="closeViewer"
    />
  </div>
</template>

<script>
// src/components/GlobalMenuButton.vue
import MenuViewer from "@/components/MenuViewer.vue";

export default {
  name: "GlobalMenuButton",
  components: {
    MenuViewer,
  },
  data() {
    return {
      // Controls expansion of the menu selection panel
      isExpanded: false,
      // Menu viewer modal state
      viewerOpen: false,
      // Index of menu to open (0=brunch, 1=dinner, 2=dessert, 3=drinks)
      initialMenuIndex: 0,
      // Menu images for thumbnails (first image of each menu)
      menuImages: {
        brunch: ["/One-Squared-Menus-Dec-2025/One-Squared-Brunch-Menu.jpg"],
        dinner: ["/One-Squared-Menus-Dec-2025/One-squared-Dinner-Menu.jpeg"],
        dessert: ["/One-Squared-Menus-Dec-2025/One-Squared-Dessert-Menu.jpg"],
        drinks: ["/One-Squared-Menus-Dec-2025/Drinks-Menu-Page-1.jpg"],
      },
      // Menu type to index mapping
      menuIndexMap: {
        brunch: 0,
        dinner: 1,
        dessert: 2,
        drinks: 3,
      },
    };
  },
  methods: {
    // Toggle the expanded menu selection panel
    toggleExpanded() {
      this.isExpanded = !this.isExpanded;
    },
    // Open menu viewer with specific menu type
    openMenu(menuType) {
      this.initialMenuIndex = this.menuIndexMap[menuType];
      this.isExpanded = false; // Close the selection panel
      this.viewerOpen = true;
    },
    // Close menu viewer
    closeViewer() {
      this.viewerOpen = false;
    },
  },
};
</script>

<style scoped>
/* Slide up animation for the expanded panel */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.slide-up-enter-to,
.slide-up-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
